import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const en = {
  "nav.dashboard": "Dashboard",
  "nav.matters": "Matters",
  "nav.timer": "Timer",
  "nav.invoices": "Invoices",
  "nav.settings": "Settings",
  "nav.sign_out": "Sign out",
  "nav.menu": "Menu",
  "search.placeholder": "Search client, matter, opposing party, case no. or ref no.",
  "search.empty": "No matters match “{q}”.",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.add": "Add",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "entry.in_invoice": "In invoice {number}",
  "entry.in_draft": "Draft {number}",
  "entry.draft_usage_edit": "Used in draft invoice {number} — your changes will be synced to the draft.",
  "entry.draft_usage_delete": " This entry is used in draft invoice {number}; deleting it will remove the line from the draft.",
  "entry.billed_locked": "Included in an issued invoice — editing and deletion are disabled.",
  "common.loading": "Loading…",
  "common.back": "Back",
  "common.notes": "Notes",
  "common.status": "Status",
  "common.priority": "Priority",
  "common.description": "Description",
  "common.due_date": "Due date",
  "common.date": "Date",
  "common.saving": "Saving…",
  "common.required": "required",

  "dashboard.title": "Dashboard",
  "dashboard.subtitle": "Overview of active matters, deadlines and tasks.",
  "dashboard.loading": "Loading dashboard…",
  "dashboard.stat_active_matters": "Active Matters",
  "dashboard.stat_upcoming": "Upcoming Deadlines",
  "dashboard.stat_overdue": "Overdue Deadlines",
  "dashboard.stat_open_tasks": "Open Tasks",
  "dashboard.stat_unbilled_time": "Unbilled Time (€)",
  "dashboard.stat_unbilled_expenses": "Unbilled Expenses (€)",
  "dashboard.reminders_reached": "Reminders reached ({n})",
  "dashboard.group_overdue": "Overdue",
  "dashboard.group_due_3": "Due within 3 days",
  "dashboard.group_due_7": "Due within 7 days",
  "dashboard.group_due_14": "Due within 14 days",
  "dashboard.group_later": "Later",
  "dashboard.no_open_deadlines": "No open deadlines. Add deadlines from a matter page.",
  "dashboard.open_tasks": "Open Tasks",
  "dashboard.no_open_tasks": "No open tasks.",
  "days.due_today": "Due today",
  "days.due_tomorrow": "Due tomorrow",
  "days.due_in": "due in {n} days",
  "days.due_in_one": "due in 1 day",
  "days.overdue_by": "overdue by {n} days",
  "days.overdue_by_one": "overdue by 1 day",
  "days.due_in_cap": "Due in {n} days",
  "days.overdue_cap": "{n} days overdue",
  "days.overdue_cap_one": "1 day overdue",
  "days.no_due_date": "No due date",

  "matters.title": "Matters",
  "matters.subtitle": "All legal matters handled by the firm.",
  "matters.new": "New Matter",
  "matters.loading": "Loading matters…",
  "matters.empty": "No matters found.",
  "matters.counts": "{d} open deadlines · {t} tasks",
  "matters.ref": "Ref {ref}",
  "matters.vs": "v.",
  "matter.back": "Back to matters",
  "matter.details": "Matter details",
  "matter.client": "Client",
  "matter.opposing_party": "Opposing party",
  "matter.internal_ref": "Internal reference no.",
  "matter.court_authority": "Court / authority",
  "matter.court_case_number": "Court case no.",
  "matter.matter_type": "Matter type",
  "matter.responsible_lawyer": "Responsible lawyer",
  "matter.billing_type": "Billing type",
  "matter.default_hourly_rate": "Default hourly rate",
  "matter.notes": "Notes",
  "matter.dropbox_folder": "Dropbox folder",
  "matter.open_dropbox": "Open Dropbox folder",
  "matter.delete_confirm": "Delete this matter and all its deadlines and tasks? This cannot be undone.",
  "matter.deleted": "Matter deleted",
  "matter.created": "Matter created",
  "matter.updated": "Matter updated",
  "matter.create_invoice": "Create invoice",
  "matter.error_back": "Back to matters",

  "deadlines.title": "Deadlines",
  "deadlines.add": "Add deadline",
  "deadlines.none": "No deadlines yet.",
  "deadlines.none_of_type": "No deadlines of this type.",
  "deadlines.all_types": "All types",
  "deadlines.reminders": "Reminders",
  "deadlines.reminder_due": "Reminder due",
  "deadlines.delete_confirm": "Delete this deadline? This cannot be undone.",
  "deadlines.deleted": "Deadline deleted",
  "deadlines.added": "Deadline added",
  "deadlines.updated": "Deadline updated",
  "tasks.title": "Tasks",
  "tasks.add": "Add task",
  "tasks.none": "No tasks yet.",
  "tasks.delete_confirm": "Delete this task? This cannot be undone.",
  "tasks.deleted": "Task deleted",
  "tasks.added": "Task added",
  "tasks.updated": "Task updated",
  "items.completed": "Completed",
  "items.reopen": "Reopen",
  "items.mark_completed": "Mark completed",
  "items.reopened": "Reopened",
  "items.completed_toast": "Marked completed",

  "form.matter_new": "New matter",
  "form.matter_edit": "Edit matter",
  "form.matter_name": "Matter name",
  "form.client": "Client",
  "form.opposing_party": "Opposing party",
  "form.internal_ref": "Internal reference no.",
  "form.court_authority": "Court / arbitration institution / authority",
  "form.case_number": "Court case no.",
  "form.matter_type": "Matter type",
  "form.matter_type_other": "Custom matter type description",
  "form.matter_type_other_ph": "Describe the matter type",
  "form.responsible_lawyer": "Responsible lawyer",
  "form.dropbox_link": "Dropbox folder link",
  "form.short_notes": "Short notes",
  "form.billing_type": "Billing type",
  "form.default_rate": "Default hourly rate (€)",
  "form.external_links": "External links",
  "form.everything_query": "Everything search query",
  "form.obsidian_vault": "Obsidian vault",
  "form.obsidian_note": "Obsidian note / URI",
  "form.zotero_uri": "Zotero URI",
  "form.outlook_ref": "Outlook reference / link",
  "form.create_matter": "Create matter",
  "form.save_changes": "Save changes",
  "form.deadline_new": "Add deadline",
  "form.deadline_edit": "Edit deadline",
  "form.deadline_type": "Deadline type",
  "form.quick_reminders": "Quick reminders",
  "form.days_before": "{n} days before",
  "form.day_before": "1 day before",
  "form.set_due_first": "Set a due date to use quick reminders.",
  "form.custom_reminders": "Custom reminder dates",
  "form.invalid_due_date": "Enter a valid due date (DD.MM.YYYY).",
  "form.start_time": "Start time",
  "form.end_time": "End time (optional)",
  "form.task_new": "Add task",
  "form.task_edit": "Edit task",
  "form.add_deadline": "Add deadline",
  "form.add_task": "Add task",

  "val.Active": "Active",
  "val.Pending": "Pending",
  "val.Closed": "Closed",
  "val.Open": "Open",
  "val.Completed": "Completed",
  "val.High": "High",
  "val.Medium": "Medium",
  "val.Low": "Low",
  "val.Procedural deadline": "Procedural deadline",
  "val.Court hearing": "Court hearing",
  "val.Client deadline": "Client deadline",
  "val.Internal deadline": "Internal deadline",
  "val.Other": "Other",
  "val.Civil litigation": "Civil litigation",
  "val.Arbitration": "Arbitration",
  "val.Maritime / Shipping": "Maritime / Shipping",
  "val.Transport / CMR": "Transport / CMR",
  "val.Insolvency": "Insolvency",
  "val.Criminal": "Criminal",
  "val.Administrative": "Administrative",
  "val.M&A / Corporate": "M&A / Corporate",
  "val.Advisory": "Advisory",
  "val.Hourly": "Hourly",
  "val.Fixed Fee": "Fixed Fee",
  "val.Non-billable": "Non-billable",
  "val.Court fee": "Court fee",
  "val.Bailiff": "Bailiff",
  "val.Translation": "Translation",
  "val.Expert": "Expert",
  "val.Travel": "Travel",
  "val.Courier/Postage": "Courier/Postage",
  "val.Registry fee": "Registry fee",
  "val.Draft": "Draft",
  "val.Issued": "Issued",
  "val.Paid": "Paid",
  "val.Cancelled": "Cancelled",
  "val.standard": "21% VAT",
  "val.zero": "0% VAT",
  "val.reverse_charge": "Reverse charge",
  "val.Yes": "Yes",
  "val.No": "No",
  "val.Unbilled": "Unbilled",
  "val.Billed": "Billed",

  "time.title": "Time",
  "time.add": "Add time entry",
  "time.add_time": "Add Time",
  "timer.manual_title": "Manual time entry",
  "timer.recent": "Recent time entries",
  "nav.calendar": "Calendar",
  "calendar.title": "Calendar",
  "calendar.today": "Today",
  "calendar.month": "Month",
  "calendar.week": "Week",
  "calendar.day": "Day",
  "calendar.new_event": "New event",
  "calendar.create_for": "Create for {date}",
  "calendar.event_type": "Event type",
  "calendar.kind_deadline": "Deadline",
  "calendar.kind_hearing": "Court hearing",
  "calendar.kind_task": "Task",
  "calendar.matter": "Matter",
  "calendar.select_matter": "Select a matter",
  "calendar.continue": "Continue",
  "calendar.no_events": "No events",
  "calendar.more": "+{n} more",
  "calendar.open_matter": "Open matter",
  "calendar.all_day": "All-day",
  "time.none": "No time entries yet. Add one manually or use the Timer.",
  "time.actual": "Actual",
  "time.billable": "Billable",
  "time.per_hour": "/ h",
  "time.billable_yes": "Billable",
  "time.billable_no": "Non-billable",
  "time.dialog_new": "Add time entry",
  "time.dialog_edit": "Edit time entry",
  "time.actual_label": "Actual time",
  "time.actual_hint": "HH:MM, decimal hours or minutes",
  "time.actual_placeholder": "1:30 or 1.5 or 90",
  "time.invalid_actual": "Enter actual time as HH:MM (1:30), decimal hours (1.5) or minutes (90).",
  "time.rate": "Hourly rate (€)",
  "time.invalid_rate": "Enter a valid hourly rate.",
  "time.invalid_date": "Enter a valid date (DD.MM.YYYY).",
  "time.preview_billable": "Billable time",
  "time.preview_amount": "Amount",
  "time.fixed_fee_note": "{type} matter — time is recorded for internal tracking only, no invoice amount.",
  "time.add_entry": "Add entry",
  "time.delete_confirm": "Delete this time entry? This cannot be undone.",
  "time.deleted": "Time entry deleted",
  "time.added": "Time entry added",
  "time.updated": "Time entry updated",
  "time.billing_status": "Billing status",
  "time.fixed_amount": "Fixed amount (€ excl. VAT)",
  "time.vat_rate": "VAT rate (%)",
  "time.fixed_required": "Enter a valid fixed amount.",
  "time.invalid_vat": "Enter a valid VAT rate (0–100).",
  "time.actual_optional": "Actual time (optional)",
  "time.actual_optional_hint": "Internal tracking only — no hourly charge.",
  "time.fixed_note": "Fixed fee — amount is independent of time spent.",
  "time.vat_short": "VAT {rate}%",

  "expenses.title": "Expenses",
  "expenses.add": "Add expense",
  "expenses.none": "No expenses yet.",
  "expenses.category": "Category",
  "expenses.category_other": "Custom category description",
  "expenses.amount": "Amount (€)",
  "expenses.vat": "VAT (€), optional",
  "expenses.billable_to_client": "Billable to client",
  "expenses.billable_yes": "Billable to client",
  "expenses.billable_no": "Not billed to client",
  "expenses.dropbox_ref": "Dropbox reference / link, optional",
  "expenses.invalid_amount": "Enter a valid amount.",
  "expenses.invalid_vat": "Enter a valid VAT amount.",
  "expenses.dialog_new": "Add expense",
  "expenses.dialog_edit": "Edit expense",
  "expenses.delete_confirm": "Delete this expense? This cannot be undone.",
  "expenses.deleted": "Expense deleted",
  "expenses.added": "Expense added",
  "expenses.updated": "Expense updated",
  "expenses.dropbox_link": "Dropbox ref",

  "summary.title": "Financial summary",
  "summary.total_billable_time": "Total billable time",
  "summary.value_billable_time": "Value of billable time",
  "summary.unbilled_time": "Unbilled time value",
  "summary.total_billable_expenses": "Total billable expenses",
  "summary.unbilled_expenses": "Unbilled expenses",

  "timer.title": "Timer",
  "timer.subtitle": "Track time against a matter. Stopping the timer creates a time entry automatically (billable time rounds up to 6-minute increments).",
  "timer.recording": "Recording time",
  "timer.stop": "Stop Timer",
  "timer.start": "Start Timer",
  "timer.select_matter": "Select a matter",
  "timer.matter": "Matter",
  "timer.short_description": "Short description",
  "timer.description_ph": "e.g. Drafting response to claim",
  "timer.keep_running": "The timer keeps running while you navigate the app.",
  "timer.select_first": "Select a matter first",
  "timer.already_running": "A timer is already running. Stop it first.",
  "timer.started": "Timer started",
  "timer.saved": "Time entry saved — {h} h billable, €{amount}",
  "timer.widget_running": "Timer running",
  "timer.stop_short": "Stop",
  "timer.saving": "Saving…",

  "ext.title": "External Links",
  "ext.dropbox": "Dropbox",
  "ext.everything": "Search in Everything",
  "ext.zotero": "Open in Zotero",
  "ext.obsidian": "Open in Obsidian",
  "ext.outlook": "Open in Outlook",
  "ext.everything_hint": "If nothing opened, the Everything URL protocol (es:) must be installed and enabled on this Windows computer. The search runs locally — nothing was sent anywhere.",
  "ext.edit_hint": "Configure links via Edit.",
  "ext.add_link": "Add link",
  "ext.not_configured": "Not configured",

  "cal.add": "Add to Outlook Calendar",
  "cal.open": "Open in Calendar",
  "cal.title_label": "Event title",
  "cal.kind_allday": "All-day event",
  "cal.kind_timed": "Timed event",
  "cal.location": "Location",
  "cal.reminder": "Calendar reminder",
  "cal.reminder_none": "None",
  "cal.reminder_days": "{n} days before",
  "cal.reminder_day": "1 day before",
  "cal.note": "A .ics calendar file will be downloaded — open it to add the event to Outlook. The RPLAW deadline is not changed.",
  "cal.prefix_deadline": "DEADLINE",
  "cal.prefix_hearing": "COURT HEARING",
  "cal.downloaded": "Calendar file downloaded",

  "invoices.title": "Invoices",
  "invoices.subtitle": "Issue invoices from unbilled time entries and expenses.",
  "invoices.new": "New invoice",
  "invoices.loading": "Loading invoices…",
  "invoices.empty": "No invoices found.",
  "invoices.search_ph": "Search no., client or matter",
  "invoices.col_number": "Invoice No.",
  "invoices.col_date": "Date",
  "invoices.col_client": "Client",
  "invoices.col_matter": "Matter",
  "invoices.col_amount": "Amount",
  "invoices.col_status": "Status",
  "invoices.col_due": "Due date",
  "invoices.select_matter": "Select the matter to invoice.",
  "invoices.matter": "Matter",
  "invoices.continue": "Continue",
  "invoices.filter_all": "All",

  "invoice.new": "New invoice",
  "invoice.edit_draft": "Edit draft {number}",
  "invoice.matter_label": "Matter",
  "invoice.details": "Invoice details",
  "invoice.number": "Invoice number",
  "invoice.date": "Invoice date",
  "invoice.due": "Due date",
  "invoice.currency": "Currency",
  "invoice.language": "Language",
  "invoice.lang_lt": "Lithuanian",
  "invoice.lang_en": "English",
  "invoice.language_saved": "Invoice language updated",
  "nav.receipts": "Cash Receipts",
  "receipts.title": "Cash Receipts",
  "receipts.search": "Search number, client or invoice…",
  "receipts.all_statuses": "All statuses",
  "receipts.number": "Receipt No.", "receipts.date": "Date", "receipts.client": "Client",
  "receipts.invoice": "Invoice", "receipts.amount": "Amount", "receipts.status": "Status",
  "receipts.empty": "No cash receipts yet. To issue your first receipt, click “New Receipt”.",
  "receipts.actions": "Actions",
  "receipts.new": "New Receipt",
  "receipts.new_title": "New cash receipt",
  "receipts.select_invoice": "Invoice (issued, with outstanding amount)",
  "receipts.search_invoice": "Search invoice no. or client…",
  "receipts.no_invoices": "No issued invoices found",
  "receipts.change": "Change",
  "receipts.outstanding": "Outstanding",
  "receipts.payment_amount": "Amount received",
  "receipts.register": "Register payment & create receipt",
  "receipts.created": "Cash payment registered and receipt draft created",
  "receipts.preview": "Cash receipt",
  "receipts.print": "Print",
  "receipts.view": "View",
  "receipts.payer": "Payer",
  "receipts.method": "Method",
  "receipts.created_by": "Created by",
  "receipts.issued_at": "Issued",
  "receipts.cancelled_info": "Cancelled",
  "receipts.draft_hint": "Draft — issue it to assign a number and generate the PDF.",
  "receipts.delete_draft": "Delete draft",
  "receipts.issue": "Issue", "receipts.issued": "Receipt issued",
  "receipts.cancel": "Cancel", "receipts.cancel_prompt": "Cancellation reason:",
  "receipts.cancelled": "Receipt cancelled",
  "receipts.delete_draft_confirm": "Delete this draft receipt?",
  "receipts.deleted": "Draft receipt deleted",
  "receipts.date_from": "Date from", "receipts.date_to": "Date to",
  "pay.method_bank": "Bank transfer", "pay.method_cash": "Cash", "pay.method_other": "Other",
  "pay.issue_receipt": "Issue cash receipt (pinigų priėmimo kvitas)",
  "pay.receipt_created": "Cash receipt draft created — review and issue it under Cash Receipts",
  "pay.receipt": "Receipt", "pay.create_receipt": "Issue receipt",
  "settings.receipt_numbering": "Cash receipt numbering",
  "settings.receipt_series": "Receipt series",
  "settings.next_receipt_number": "Next receipt number",
  "invoice.delete_test_confirm": "Delete TEST invoice {number} (status: {status})? Linked time entries and expenses will be released as Unbilled and any payments removed. This cannot be undone.",
  "invoices.purge_test": "Delete test invoices",
  "invoices.purge_confirm": "Delete all {count} TEST invoices? Linked time entries and expenses will be released as Unbilled and any payments removed. This cannot be undone.",
  "invoices.purged": "Deleted {count} test invoices",
  "invoice.vat_treatment": "VAT treatment",
  "invoice.payment_link": "Payment link (optional)",
  "invoice.vat_note": "VAT note (shown on PDF)",
  "invoice.client": "Client",
  "invoice.client_code": "Client company code",
  "invoice.client_vat": "Client VAT No.",
  "invoice.client_address": "Client address",
  "invoice.internal_notes": "Internal notes (not shown on PDF)",
  "invoice.unbilled_items": "Unbilled items",
  "invoice.unbilled_time": "Unbilled time entries",
  "invoice.unbilled_expenses": "Unbilled expenses",
  "invoice.no_time": "No unbilled billable time entries.",
  "invoice.no_expenses": "No unbilled billable expenses.",
  "invoice.lines": "Invoice lines ({n})",
  "invoice.no_lines": "No lines selected yet.",
  "invoice.time_entry": "Time entry",
  "invoice.expense": "Expense",
  "invoice.totals_fees": "Legal fees (excl. VAT)",
  "invoice.totals_vat": "VAT",
  "invoice.totals_expenses": "Reimbursable expenses",
  "invoice.totals_total": "Total payable",
  "invoice.create_draft": "Create draft invoice",
  "invoice.save_draft": "Save draft",
  "invoice.select_one": "Select at least one time entry or expense.",
  "invoice.invalid_dates": "Enter valid invoice and due dates (DD.MM.YYYY).",
  "invoice.created": "Draft invoice {number} created",
  "invoice.updated": "Invoice updated",
  "invoice.only_draft_edit": "Only draft invoices can be edited",
  "invoice.back": "Back to invoices",
  "invoice.download_pdf": "Download PDF",
  "invoice.mark_issued": "Mark as Issued",
  "invoice.mark_paid": "Mark as Paid",
  "invoice.cancel_invoice": "Cancel Invoice",
  "invoice.delete": "Delete",
  "invoice.release": "Release billed entries",
  "invoice.issue_confirm": "Issue invoice {number}? Included entries will be marked as Billed.",
  "invoice.issued": "Invoice issued",
  "invoice.paid_confirm": "Mark invoice {number} as Paid?",
  "invoice.paid": "Invoice marked as paid",
  "invoice.cancel_confirm": "Cancel invoice {number}? The number stays in history. Entries remain Billed until released explicitly.",
  "invoice.cancelled": "Invoice cancelled",
  "invoice.delete_confirm": "Delete this draft invoice? Its entries will be released back to Unbilled.",
  "invoice.deleted": "Draft invoice deleted",
  "invoice.release_confirm": "Release the billed time entries and expenses back to Unbilled?",
  "invoice.released": "Entries released to Unbilled",
  "invoice.released_note": "Billed entries have been released back to Unbilled.",
  "invoice.payment_link_title": "Payment link",
  "invoice.payment_saved": "Payment link saved",
  "invoice.no_payment_link": "No payment link set — nothing payment-related will appear on the PDF.",
  "invoice.pay": "Pay invoice",
  "invoice.services": "Legal services",
  "invoice.reimb": "Reimbursable expenses",
  "invoice.no_time_lines": "No time entries included.",
  "invoice.no_expense_lines": "No expenses included.",
  "invoice.col_unit": "Unit",
  "invoice.col_qty": "Qty",
  "invoice.col_rate": "Rate",
  "invoice.col_vat": "VAT %",
  "invoice.col_amount": "Amount (excl. VAT)",

  "settings.title": "Settings",
  "settings.subtitle": "Invoice issuer details, numbering and integrations.",
  "settings.firm": "Firm details",
  "settings.invoice_defaults": "Invoice defaults",
  "settings.integrations": "Integrations",
  "settings.firm_name": "Firm / attorney name",
  "settings.company_code": "Company / individual code",
  "settings.vat_number": "VAT number",
  "settings.address": "Address",
  "settings.telephone": "Telephone",
  "settings.email": "Email",
  "settings.bank_name": "Bank name",
  "settings.swift": "SWIFT / BIC",
  "settings.iban1": "IBAN 1",
  "settings.iban2": "IBAN 2",
  "settings.default_vat": "Default VAT rate (%)",
  "settings.payment_term": "Default payment term (days)",
  "settings.invoice_prefix": "Invoice prefix",
  "settings.next_number": "Next invoice number",
  "settings.next_suggested": "Next suggested invoice: {number}",
  "settings.everything_fields": "Everything search fields",
  "settings.ef_client": "Client",
  "settings.ef_name": "Matter name",
  "settings.ef_internal_ref": "Internal reference",
  "settings.ef_case_number": "Case number",
  "settings.obsidian_vault": "Default Obsidian vault name",
  "settings.calendar_reminder": "Default calendar reminder",
  "settings.default_language": "Default application language",
  "settings.save": "Save settings",
  "settings.saved": "Settings saved",
  "settings.loading": "Loading settings…",

  "nav.reports": "Reports",
  "reports.title": "Reports",
  "reports.subtitle": "Practice management and billing reports from existing RPLAW data.",
  "reports.type.time": "Time Report",
  "reports.type.matter-activity": "Matter Activity Report",
  "reports.type.expenses": "Expense Report",
  "reports.type.unbilled": "Unbilled Work Report",
  "reports.type.invoices": "Invoice Report",
  "reports.type.revenue": "Revenue Report",
  "reports.filters": "Filters",
  "reports.quick_range": "Quick range",
  "reports.range.all": "All time",
  "reports.range.this_month": "This month",
  "reports.range.prev_month": "Previous month",
  "reports.range.this_quarter": "This quarter",
  "reports.range.this_year": "This year",
  "reports.range.prev_year": "Previous year",
  "reports.range.custom": "Custom range",
  "reports.date_from": "Date from",
  "reports.date_to": "Date to",
  "reports.client": "Client",
  "reports.client_ph": "Client name…",
  "reports.matter": "Matter",
  "reports.all": "All",
  "reports.matter_status": "Matter status",
  "reports.lawyer": "Responsible lawyer",
  "reports.billing_status": "Billing status",
  "reports.invoice_status": "Invoice status",
  "reports.group_by": "Group by",
  "reports.group_none": "No grouping",
  "reports.group_client": "Client",
  "reports.group_matter": "Matter",
  "reports.include": "Include",
  "reports.inc_time": "Time entries",
  "reports.inc_tasks": "Tasks",
  "reports.inc_deadlines": "Deadlines",
  "reports.inc_expenses": "Expenses",
  "reports.inc_invoices": "Invoices",
  "reports.select_matter": "Select a matter for this report.",
  "reports.print": "Print",
  "reports.pdf": "PDF",
  "reports.csv": "Export CSV",
  "reports.empty": "No data for the selected filters.",
  "reports.loading": "Preparing report…",
  "reports.generated": "Generated",
  "reports.period": "Period",
  "reports.totals": "Totals",
  "reports.total_actual": "Total actual time",
  "reports.total_billable": "Total billable time",
  "reports.total_value": "Total value",
  "reports.total_unbilled": "Unbilled value",
  "reports.total_amount": "Total amount",
  "reports.total_vat": "Total VAT",
  "reports.grand_total": "Grand total",
  "reports.unbilled_hours": "Unbilled hours",
  "reports.unbilled_time_value": "Unbilled time value",
  "reports.unbilled_expenses": "Unbilled expenses",
  "reports.total_potential": "Total potentially billable",
  "reports.allocated_draft": "Allocated to draft {n}",
  "reports.issued_total": "Issued invoices",
  "reports.paid_total": "Paid invoices",
  "reports.revenue_note": "Revenue is calculated from invoice statuses. Drafts and cancelled invoices are not revenue.",
  "reports.invoice_count": "Invoice count",
  "reports.invoiced_total": "Invoiced total",
  "reports.time_value": "Time value",
  "reports.expenses_total": "Expenses total",
  "reports.col.date": "Date",
  "reports.col.client": "Client",
  "reports.col.matter": "Matter",
  "reports.col.description": "Description",
  "reports.col.actual": "Actual",
  "reports.col.billable": "Billable",
  "reports.col.rate": "Rate",
  "reports.col.amount": "Amount",
  "reports.col.billing_status": "Billing",
  "reports.col.category": "Category",
  "reports.col.vat": "VAT",
  "reports.col.billable_client": "Billable to client",
  "reports.col.invoice_no": "Invoice No.",
  "reports.col.number": "Invoice No.",
  "reports.col.invoice_date": "Invoice date",
  "reports.col.due": "Due date",
  "reports.col.fees": "Fees excl. VAT",
  "reports.col.expenses": "Reimb. expenses",
  "reports.col.total": "Total",
  "reports.col.status": "Status",
  "reports.col.type": "Entry",
  "reports.col.hours": "Hours",
  "reports.col.value": "Value",
  "reports.col.allocation": "Allocation",
  "reports.kind.time": "Time entry",
  "reports.kind.expense": "Expense",
  "reports.cat.time": "Time",
  "reports.cat.task": "Task",
  "reports.cat.deadline": "Deadline",
  "reports.cat.expense": "Expense",
  "reports.cat.invoice": "Invoice",
  "settings.branding": "Branding",
  "settings.invoice_logo": "Full logo for invoices",
  "settings.app_symbol": "App symbol / logo",
  "settings.upload": "Upload",
  "settings.logo_uploaded": "Logo updated",
  "settings.logo_hint": "PNG, JPG or WEBP up to 5 MB.",

  "nav.clients": "Clients",
  "nav.import": "Data Import",
  "import.title": "Data Import",
  "import.subtitle": "Import clients and historical invoices from VMI i.SAF XML (v1.2) files, incl. Saskaitele.lt exports.",
  "import.tab_new": "New import",
  "import.tab_archive": "Imported invoices",
  "import.tab_batches": "Import history",
  "import.file_label": "i.SAF XML file",
  "import.file_hint": ".xml, up to 20 MB",
  "import.choose_file": "Choose file",
  "import.analyze": "Analyze",
  "import.analyzing": "Analyzing…",
  "import.confirm": "Confirm import",
  "import.importing": "Importing…",
  "import.no_changes": "No database changes are made until you confirm the import.",
  "import.preview_title": "Import preview",
  "import.customers": "Customers",
  "import.invoices": "Invoices",
  "import.total": "Total found",
  "import.new_records": "New",
  "import.duplicates": "Possible duplicates",
  "import.invalid": "Invalid",
  "import.incomplete": "Incomplete (no codes)",
  "import.skipped": "Will be skipped",
  "import.reason_label": "Reason",
  "import.matched_with": "Matches",
  "import.reason.missing_name": "Missing name",
  "import.reason.missing_invoice_no": "Missing invoice number",
  "import.reason.missing_invoice_date": "Missing invoice date",
  "import.reason.invalid_date": "Invalid date",
  "import.reason.registration_number": "Registration code already exists",
  "import.reason.vat_number": "VAT number already exists",
  "import.reason.name": "Name already exists",
  "import.reason.within_file": "Duplicate within the file",
  "import.reason.invoice_exists": "Invoice already imported",
  "import.col_name": "Name",
  "import.col_legacy_id": "Source ID",
  "import.col_reg": "Reg. code",
  "import.col_vat": "VAT No.",
  "import.col_country": "Country",
  "import.col_invoice_no": "Invoice No.",
  "import.col_date": "Date",
  "import.col_type": "Type",
  "import.col_customer": "Customer",
  "import.col_taxable": "Taxable value",
  "import.col_vat_amount": "VAT",
  "import.col_gross": "Gross total",
  "import.col_currency": "Currency",
  "import.col_due_date": "Due date",
  "import.result_title": "Import completed",
  "import.result_customers": "Customers imported",
  "import.result_invoices": "Invoices imported",
  "import.result_skipped": "Skipped",
  "import.error_parse": "Could not parse the XML file. Make sure it is a valid i.SAF 1.2 file.",
  "import.error_empty": "No customers or invoices found in the file.",
  "import.empty_archive": "No imported invoices yet.",
  "import.empty_batches": "No imports yet.",
  "import.batch_file": "File",
  "import.batch_date": "Imported at",
  "import.search_archive": "Search by invoice no. or customer…",
  "clients.title": "Clients",
  "clients.subtitle": "Client registry, incl. customers imported from i.SAF.",
  "clients.search": "Search by name or code…",
  "clients.empty": "No clients yet. Import from i.SAF to get started.",
  "clients.source": "Source",
  "clients.total": "{n} clients",
  "clients.back": "Back to clients",
  "clients.basic_data": "Client data",
  "clients.imported_invoices": "Imported invoices",
  "clients.no_invoices": "No imported invoices for this client.",
  "clients.totals": "Totals",
  "clients.filter_no": "Filter by invoice no.…",
  "clients.date_from": "Date from",
  "clients.date_to": "Date to",
  "clients.view": "View",
  "import.col_client_link": "Client",
  "nav.receivables": "Receivables",
  "pay.title": "Payments",
  "pay.add": "Add payment",
  "pay.edit": "Edit payment",
  "pay.delete": "Delete payment",
  "pay.delete_confirm": "Delete this payment?",
  "pay.date": "Payment date",
  "pay.amount": "Amount",
  "pay.method": "Method / reference",
  "pay.note": "Note",
  "pay.total_paid": "Total paid",
  "pay.outstanding": "Outstanding balance",
  "pay.status.unpaid": "Unpaid",
  "pay.status.partial": "Partially paid",
  "pay.status.paid": "Paid",
  "pay.status.overdue": "Overdue",
  "pay.empty": "No payments recorded yet.",
  "pay.overpay_warning": "Total payments ({paid}) would exceed the invoice total ({gross}). Confirm anyway?",
  "pay.added": "Payment added",
  "pay.updated": "Payment updated",
  "pay.deleted": "Payment deleted",
  "pay.save": "Save payment",
  "pay.days_overdue": "Days overdue",
  "recv.title": "Receivables",
  "recv.subtitle": "Issued RPLAW invoices and their payment status. Imported i.SAF invoices are not included.",
  "recv.filter_client": "Client…",
  "recv.filter_no": "Invoice no.…",
  "recv.filter_status": "Status",
  "recv.all": "All",
  "recv.outstanding_only": "Outstanding only",
  "recv.empty": "No invoices match the filters.",
  "recv.col_invoice": "Invoice No.",
  "recv.col_client": "Client",
  "recv.col_date": "Invoice date",
  "recv.col_due": "Due date",
  "recv.col_gross": "Gross total",
  "recv.col_paid": "Paid",
  "recv.col_outstanding": "Outstanding",
  "recv.col_status": "Status",
  "recv.col_days_overdue": "Days overdue",
  "dashboard.stat_outstanding": "Outstanding invoices",
  "dashboard.stat_overdue_invoices": "Overdue invoices",
  "reports.payments_received": "Payments received",
  "reports.payments_note": "Payments received are based on actual payment records and are kept separate from invoiced revenue.",
  "reports.col_method": "Method",
  "clients.rplaw_invoices": "RPLAW invoices",
  "clients.billed": "Billed",
  "clients.paid": "Paid",
  "clients.outstanding": "Outstanding",
  "clients.no_rplaw_invoices": "No RPLAW invoices for this client.",
  "clients.add_matter": "New matter",
  "pay.status.unknown": "Unknown",
  "pay.settled": "Settlement confirmed (no payment transaction)",
  "imp.back": "Back to archive",
  "imp.original_data": "Original accounting data (from i.SAF, read-only)",
  "imp.payment_info": "Payment information (entered in RPLAW)",
  "imp.mark_paid": "Mark as paid",
  "imp.mark_paid_confirm": "Mark this invoice as fully paid? This records a historical settlement confirmation without creating a payment transaction.",
  "imp.mark_unpaid": "Mark as unpaid/open",
  "imp.mark_unpaid_confirm": "Mark this invoice as unpaid? The outstanding balance becomes the full gross amount.",
  "imp.mark_unknown": "Back to Unknown",
  "imp.mark_unknown_confirm": "Return the status to Unknown? Payment history is preserved.",
  "imp.mark_unknown_blocked": "Payment records exist. Reconcile (delete) the payments before returning to Unknown.",
  "imp.status_set": "Status updated",
  "recv.filter_source": "Source",
  "recv.source.all": "All",
  "recv.source.rplaw": "RPLAW",
  "recv.source.isaf": "Imported i.SAF",
  "clients.imp_confirmed_outstanding": "Confirmed outstanding",
  "clients.imp_paid": "Paid (imported)",
  "clients.imp_unknown": "Unknown status",
  "reports.payments_split_rplaw": "from RPLAW invoices",
  "reports.payments_split_imported": "from imported invoices",
  "clients.status": "Status",
  "clients.mark_inactive": "Mark inactive",
  "clients.reactivate": "Reactivate",
  "clients.status_updated": "Client status updated",
  "clients.matters": "Matters",
  "clients.no_matters": "No linked matters.",
  "clients.created": "Client created",
  "clients.matter_name": "Matter name",
  "form.client_create_new": "Create new client",
  "form.client_include_inactive": "Include inactive clients",
  "form.client_create_title": "New client",
  "form.client_no_results": "No matching clients",
  "form.client_create_anyway": "Create anyway",
  "matters.close": "Close matter",
  "matters.archive": "Archive",
  "matters.reopen": "Reopen",
  "matters.close_confirm": "Close this matter? It remains accessible and can be reopened.",
  "matters.archive_confirm": "Archive this matter? It remains accessible and can be reopened.",
  "matters.closed": "Matter closed",
  "matters.archived": "Matter archived",
  "matters.reopened": "Matter reopened",
  "matters.reopen_confirm": "Reopen this matter? It will become active again.",
  "val.On hold": "On hold",
  "val.Archived": "Archived",
  "val.Inactive": "Inactive",

  "auth.sign_in": "Sign in",
  "auth.subtitle": "Access your matters and deadlines.",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.signing_in": "Signing in…",
  "auth.forgot": "Forgot password?",
  "auth.private": "Private — authorised users only.",
  "auth.tagline1": "Every matter. Every deadline.",
  "auth.tagline2": "Nothing missed.",
  "auth.tagline_body": "Private case and deadline management for your practice. Sign in to review active matters, upcoming court deadlines and open tasks.",
  "auth.reset_title": "Reset password",
  "auth.reset_body": "Enter your account email and we will send you a reset link.",
  "auth.send_link": "Send reset link",
  "auth.sending": "Sending…",
  "auth.check_inbox": "Check your inbox",
  "auth.reset_sent": "If that email is registered, a reset link has been sent. The link expires in 1 hour.",
  "auth.back_login": "Back to sign in",
  "auth.new_password": "Set a new password",
  "auth.min_chars": "Minimum 8 characters.",
  "auth.new_password_label": "New password",
  "auth.confirm_password": "Confirm password",
  "auth.mismatch": "Passwords do not match.",
  "auth.reset_btn": "Reset password",
  "auth.invalid_link": "Invalid link",
  "auth.missing_token": "This reset link is missing its token. Request a new one.",
  "auth.loading": "Loading…",
};

const lt = {
  "nav.dashboard": "Suvestinė",
  "nav.matters": "Bylos",
  "nav.timer": "Laiko sekimas",
  "nav.invoices": "Sąskaitos",
  "nav.settings": "Nustatymai",
  "nav.sign_out": "Atsijungti",
  "nav.menu": "Meniu",
  "search.placeholder": "Ieškoti pagal klientą, bylą, priešingą šalį, bylos ar ref. nr.",
  "search.empty": "Nėra bylų, atitinkančių „{q}“.",
  "common.save": "Išsaugoti",
  "common.cancel": "Atšaukti",
  "common.add": "Pridėti",
  "common.delete": "Ištrinti",
  "common.edit": "Redaguoti",
  "entry.in_invoice": "Sąskaitoje {number}",
  "entry.in_draft": "Juodraštis {number}",
  "entry.draft_usage_edit": "Įrašas naudojamas sąskaitos juodraštyje {number} — pakeitimai bus sinchronizuoti į juodraštį.",
  "entry.draft_usage_delete": " Šis įrašas naudojamas sąskaitos juodraštyje {number}; ištrynus, eilutė bus pašalinta iš juodraščio.",
  "entry.billed_locked": "Įtraukta į išrašytą sąskaitą — redaguoti ir trinti negalima.",
  "common.loading": "Kraunama…",
  "common.back": "Atgal",
  "common.notes": "Pastabos",
  "common.status": "Būsena",
  "common.priority": "Prioritetas",
  "common.description": "Aprašymas",
  "common.due_date": "Terminas",
  "common.date": "Data",
  "common.saving": "Saugoma…",
  "common.required": "privaloma",

  "dashboard.title": "Suvestinė",
  "dashboard.subtitle": "Aktyvių bylų, terminų ir užduočių apžvalga.",
  "dashboard.loading": "Kraunama suvestinė…",
  "dashboard.stat_active_matters": "Aktyvios bylos",
  "dashboard.stat_upcoming": "Artėjantys terminai",
  "dashboard.stat_overdue": "Pravėluoti terminai",
  "dashboard.stat_open_tasks": "Neatliktos užduotys",
  "dashboard.stat_unbilled_time": "Neišrašytas laikas (€)",
  "dashboard.stat_unbilled_expenses": "Neišrašytos išlaidos (€)",
  "dashboard.reminders_reached": "Pasiekti priminimai ({n})",
  "dashboard.group_overdue": "Pravėluota",
  "dashboard.group_due_3": "Terminas per 3 dienas",
  "dashboard.group_due_7": "Terminas per 7 dienas",
  "dashboard.group_due_14": "Terminas per 14 dienų",
  "dashboard.group_later": "Vėliau",
  "dashboard.no_open_deadlines": "Nėra neatliktų terminų. Terminus pridėkite bylos puslapyje.",
  "dashboard.open_tasks": "Neatliktos užduotys",
  "dashboard.no_open_tasks": "Nėra neatliktų užduočių.",
  "days.due_today": "Terminas šiandien",
  "days.due_tomorrow": "Terminas rytoj",
  "days.due_in": "liko {n} d.",
  "days.due_in_one": "liko 1 diena",
  "days.overdue_by": "pravėluota {n} d.",
  "days.overdue_by_one": "pravėluota 1 dieną",
  "days.due_in_cap": "Terminas po {n} d.",
  "days.overdue_cap": "Pravėluota {n} d.",
  "days.overdue_cap_one": "Pravėluota 1 d.",
  "days.no_due_date": "Be termino",

  "matters.title": "Bylos",
  "matters.subtitle": "Visos įmonės vedamos bylos.",
  "matters.new": "Nauja byla",
  "matters.loading": "Kraunamos bylos…",
  "matters.empty": "Bylų nerasta.",
  "matters.counts": "{d} neatlikti terminai · {t} užduotys",
  "matters.ref": "Ref. {ref}",
  "matters.vs": "su",
  "matter.back": "Atgal į bylas",
  "matter.details": "Bylos duomenys",
  "matter.client": "Klientas",
  "matter.opposing_party": "Priešinga šalis",
  "matter.internal_ref": "Vidinis nr.",
  "matter.court_authority": "Teismas / institucija",
  "matter.court_case_number": "Bylos nr. teisme",
  "matter.matter_type": "Bylos tipas",
  "matter.responsible_lawyer": "Atsakingas teisininkas",
  "matter.billing_type": "Apmokėjimo tipas",
  "matter.default_hourly_rate": "Numatytasis valandinis įkainis",
  "matter.notes": "Pastabos",
  "matter.dropbox_folder": "Dropbox aplankas",
  "matter.open_dropbox": "Atidaryti Dropbox aplanką",
  "matter.delete_confirm": "Ištrinti bylą su visais terminais ir užduotimis? Veiksmas neatšaukiamas.",
  "matter.deleted": "Byla ištrinta",
  "matter.created": "Byla sukurta",
  "matter.updated": "Byla atnaujinta",
  "matter.create_invoice": "Kurti sąskaitą",
  "matter.error_back": "Atgal į bylas",

  "deadlines.title": "Terminai",
  "deadlines.add": "Pridėti terminą",
  "deadlines.none": "Terminų dar nėra.",
  "deadlines.none_of_type": "Nėra šio tipo terminų.",
  "deadlines.all_types": "Visi tipai",
  "deadlines.reminders": "Priminimai",
  "deadlines.reminder_due": "Priminimo laikas",
  "deadlines.delete_confirm": "Ištrinti terminą? Veiksmas neatšaukiamas.",
  "deadlines.deleted": "Terminas ištrintas",
  "deadlines.added": "Terminas pridėtas",
  "deadlines.updated": "Terminas atnaujintas",
  "tasks.title": "Užduotys",
  "tasks.add": "Pridėti užduotį",
  "tasks.none": "Užduočių dar nėra.",
  "tasks.delete_confirm": "Ištrinti užduotį? Veiksmas neatšaukiamas.",
  "tasks.deleted": "Užduotis ištrinta",
  "tasks.added": "Užduotis pridėta",
  "tasks.updated": "Užduotis atnaujinta",
  "items.completed": "Įvykdyta",
  "items.reopen": "Atnaujinti",
  "items.mark_completed": "Pažymėti įvykdyta",
  "items.reopened": "Atnaujinta",
  "items.completed_toast": "Pažymėta įvykdyta",

  "form.matter_new": "Nauja byla",
  "form.matter_edit": "Redaguoti bylą",
  "form.matter_name": "Bylos pavadinimas",
  "form.client": "Klientas",
  "form.opposing_party": "Priešinga šalis",
  "form.internal_ref": "Vidinis nr.",
  "form.court_authority": "Teismas / arbitražo institucija / institucija",
  "form.case_number": "Bylos nr. teisme",
  "form.matter_type": "Bylos tipas",
  "form.matter_type_other": "Bylos tipo aprašymas",
  "form.matter_type_other_ph": "Aprašykite bylos tipą",
  "form.responsible_lawyer": "Atsakingas teisininkas",
  "form.dropbox_link": "Dropbox aplanko nuoroda",
  "form.short_notes": "Trumpi užrašai",
  "form.billing_type": "Apmokėjimo tipas",
  "form.default_rate": "Numatytasis valandinis įkainis (€)",
  "form.external_links": "Išorinės nuorodos",
  "form.everything_query": "Everything paieškos užklausa",
  "form.obsidian_vault": "Obsidian vault",
  "form.obsidian_note": "Obsidian užrašas / URI",
  "form.zotero_uri": "Zotero URI",
  "form.outlook_ref": "Outlook nuoroda / nuoroda į laišką",
  "form.create_matter": "Sukurti bylą",
  "form.save_changes": "Išsaugoti pakeitimus",
  "form.deadline_new": "Pridėti terminą",
  "form.deadline_edit": "Redaguoti terminą",
  "form.deadline_type": "Termino tipas",
  "form.quick_reminders": "Greiti priminimai",
  "form.days_before": "likus {n} d.",
  "form.day_before": "likus 1 d.",
  "form.set_due_first": "Greitiems priminimams pirmiausia nustatykite terminą.",
  "form.custom_reminders": "Pasirinktinės priminimų datos",
  "form.invalid_due_date": "Įveskite teisingą terminą (DD.MM.YYYY).",
  "form.start_time": "Pradžios laikas",
  "form.end_time": "Pabaigos laikas (neprivaloma)",
  "form.task_new": "Pridėti užduotį",
  "form.task_edit": "Redaguoti užduotį",
  "form.add_deadline": "Pridėti terminą",
  "form.add_task": "Pridėti užduotį",

  "val.Active": "Aktyvi",
  "val.Pending": "Laukiama",
  "val.Closed": "Užbaigta",
  "val.Open": "Atvira",
  "val.Completed": "Įvykdyta",
  "val.High": "Aukštas",
  "val.Medium": "Vidutinis",
  "val.Low": "Žemas",
  "val.Procedural deadline": "Procesinis terminas",
  "val.Court hearing": "Teismo posėdis",
  "val.Client deadline": "Kliento terminas",
  "val.Internal deadline": "Vidinis terminas",
  "val.Other": "Kita",
  "val.Civil litigation": "Civilinės bylos",
  "val.Arbitration": "Arbitražas",
  "val.Maritime / Shipping": "Jūrų / laivybos teisė",
  "val.Transport / CMR": "Transportas / CMR",
  "val.Insolvency": "Nemokumas",
  "val.Criminal": "Baudžiamosios bylos",
  "val.Administrative": "Administracinės bylos",
  "val.M&A / Corporate": "M&A / Komercinė teisė",
  "val.Advisory": "Konsultacijos",
  "val.Hourly": "Valandinis",
  "val.Fixed Fee": "Fiksuotas atlygis",
  "val.Non-billable": "Neapmokestinama",
  "val.Court fee": "Žymininkas mokestis",
  "val.Bailiff": "Antstolis",
  "val.Translation": "Vertimas",
  "val.Expert": "Ekspertizė",
  "val.Travel": "Kelionė",
  "val.Courier/Postage": "Kurjeris / paštas",
  "val.Registry fee": "Registro mokestis",
  "val.Draft": "Juodraštis",
  "val.Issued": "Išrašyta",
  "val.Paid": "Apmokėta",
  "val.Cancelled": "Atšaukta",
  "val.standard": "21% PVM",
  "val.zero": "0% PVM",
  "val.reverse_charge": "Atvirkštinis apmokestinimas",
  "val.Yes": "Taip",
  "val.No": "Ne",
  "val.Unbilled": "Neišrašyta",
  "val.Billed": "Išrašyta",

  "time.title": "Laikas",
  "time.add": "Pridėti laiko įrašą",
  "time.add_time": "Pridėti laiką",
  "timer.manual_title": "Laiko įvedimas rankiniu būdu",
  "timer.recent": "Naujausi laiko įrašai",
  "nav.calendar": "Kalendorius",
  "calendar.title": "Kalendorius",
  "calendar.today": "Šiandien",
  "calendar.month": "Mėnuo",
  "calendar.week": "Savaitė",
  "calendar.day": "Diena",
  "calendar.new_event": "Naujas įvykis",
  "calendar.create_for": "Kurti: {date}",
  "calendar.event_type": "Įvykio tipas",
  "calendar.kind_deadline": "Terminas",
  "calendar.kind_hearing": "Teismo posėdis",
  "calendar.kind_task": "Užduotis",
  "calendar.matter": "Byla",
  "calendar.select_matter": "Pasirinkite bylą",
  "calendar.continue": "Toliau",
  "calendar.no_events": "Įvykių nėra",
  "calendar.more": "+{n} daugiau",
  "calendar.open_matter": "Atidaryti bylą",
  "calendar.all_day": "Visos dienos",
  "time.none": "Laiko įrašų dar nėra. Pridėkite rankiniu būdu arba naudokite laiko sekimą.",
  "time.actual": "Faktinis",
  "time.billable": "Apmokamas",
  "time.per_hour": "/ val.",
  "time.billable_yes": "Apmokamas",
  "time.billable_no": "Neapmokamas",
  "time.dialog_new": "Pridėti laiko įrašą",
  "time.dialog_edit": "Redaguoti laiko įrašą",
  "time.actual_label": "Faktinis laikas",
  "time.actual_hint": "VV:MM, dešimtainės valandos arba minutės",
  "time.actual_placeholder": "1:30 arba 1.5 arba 90",
  "time.invalid_actual": "Įveskite laiką kaip VV:MM (1:30), dešimtaines valandas (1.5) arba minutes (90).",
  "time.rate": "Valandinis įkainis (€)",
  "time.invalid_rate": "Įveskite teisingą valandinį įkainį.",
  "time.invalid_date": "Įveskite teisingą datą (DD.MM.YYYY).",
  "time.preview_billable": "Apmokamas laikas",
  "time.preview_amount": "Suma",
  "time.fixed_fee_note": "„{type}“ byla — laikas fiksuojamas tik vidiniam sekimui, sąskaitos suma nesidaro.",
  "time.add_entry": "Pridėti įrašą",
  "time.delete_confirm": "Ar tikrai norite ištrinti šį įrašą? Šio veiksmo atšaukti negalima.",
  "time.deleted": "Laiko įrašas ištrintas",
  "time.added": "Laiko įrašas pridėtas",
  "time.updated": "Laiko įrašas atnaujintas",
  "time.billing_status": "Atsiskaitymo būsena",
  "time.fixed_amount": "Fiksuota suma (€ be PVM)",
  "time.vat_rate": "PVM tarifas (%)",
  "time.fixed_required": "Įveskite teisingą fiksuotą sumą.",
  "time.invalid_vat": "Įveskite teisingą PVM tarifą (0–100).",
  "time.actual_optional": "Faktiškai sugaištas laikas (neprivaloma)",
  "time.actual_optional_hint": "Tik vidinei apskaitai — valandinis atlygis neskaičiuojamas.",
  "time.fixed_note": "Fiksuotas atlygis — suma nepriklauso nuo sugaišto laiko.",
  "time.vat_short": "PVM {rate}%",

  "expenses.title": "Išlaidos",
  "expenses.add": "Pridėti išlaidą",
  "expenses.none": "Išlaidų dar nėra.",
  "expenses.category": "Kategorija",
  "expenses.category_other": "Pasirinktinis kategorijos aprašymas",
  "expenses.amount": "Suma (€)",
  "expenses.vat": "PVM (€), neprivaloma",
  "expenses.billable_to_client": "Priskirtina klientui",
  "expenses.billable_yes": "Priskirtina klientui",
  "expenses.billable_no": "Nepriskirtina klientui",
  "expenses.dropbox_ref": "Dropbox nuoroda / saitas, neprivaloma",
  "expenses.invalid_amount": "Įveskite teisingą sumą.",
  "expenses.invalid_vat": "Įveskite teisingą PVM sumą.",
  "expenses.dialog_new": "Pridėti išlaidą",
  "expenses.dialog_edit": "Redaguoti išlaidą",
  "expenses.delete_confirm": "Ar tikrai norite ištrinti šį įrašą? Šio veiksmo atšaukti negalima.",
  "expenses.deleted": "Išlaida ištrinta",
  "expenses.added": "Išlaida pridėta",
  "expenses.updated": "Išlaida atnaujinta",
  "expenses.dropbox_link": "Dropbox nuoroda",

  "summary.title": "Finansinė suvestinė",
  "summary.total_billable_time": "Visas apmokamas laikas",
  "summary.value_billable_time": "Apmokamo laiko vertė",
  "summary.unbilled_time": "Neišrašyto laiko vertė",
  "summary.total_billable_expenses": "Visos klientui priskirtinos išlaidos",
  "summary.unbilled_expenses": "Neišrašytos išlaidos",

  "timer.title": "Laiko sekimas",
  "timer.subtitle": "Fiksuokite laiką bylos darbams. Sustabdžius laiko matuoklį automatiškai sukuriamas laiko įrašas (apmokamas laikas apvalinamas į viršų 6 min. tikslumu).",
  "timer.recording": "Laikas fiksuojamas",
  "timer.stop": "Sustabdyti",
  "timer.start": "Pradėti",
  "timer.select_matter": "Pasirinkite bylą",
  "timer.matter": "Byla",
  "timer.short_description": "Trumpas aprašymas",
  "timer.description_ph": "pvz., Atsiliepimo į ieškinį rengimas",
  "timer.keep_running": "Laiko matuoklis veikia ir naršant kituose puslapiuose.",
  "timer.select_first": "Pirmiausia pasirinkite bylą",
  "timer.already_running": "Laiko matuoklis jau veikia. Pirmiausia jį sustabdykite.",
  "timer.started": "Laiko fiksavimas pradėtas",
  "timer.saved": "Laiko įrašas išsaugotas — {h} val. apmokama, €{amount}",
  "timer.widget_running": "Laikas fiksuojamas",
  "timer.stop_short": "Stabdyti",
  "timer.saving": "Saugoma…",

  "ext.title": "Išorinės nuorodos",
  "ext.dropbox": "Dropbox",
  "ext.everything": "Ieškoti su Everything",
  "ext.zotero": "Atidaryti Zotero",
  "ext.obsidian": "Atidaryti Obsidian",
  "ext.outlook": "Atidaryti Outlook",
  "ext.everything_hint": "Jei niekas neatsidarė, šiame Windows kompiuteryje turi būti įdiegtas ir įjungtas Everything URL protokolas (es:). Paieška atliekama vietoje — niekas niekur neišsiųsta.",
  "ext.edit_hint": "Nuorodas sukonfigūruokite per „Redaguoti“.",
  "ext.add_link": "Pridėti nuorodą",
  "ext.not_configured": "Nekonfigūruota",

  "cal.add": "Pridėti į Outlook kalendorių",
  "cal.open": "Atidaryti kalendoriuje",
  "cal.title_label": "Įvykio pavadinimas",
  "cal.kind_allday": "Visos dienos įvykis",
  "cal.kind_timed": "Įvykis su laiku",
  "cal.location": "Vieta",
  "cal.reminder": "Kalendoriaus priminimas",
  "cal.reminder_none": "Be priminimo",
  "cal.reminder_days": "likus {n} d.",
  "cal.reminder_day": "likus 1 d.",
  "cal.note": "Bus atsiųstas .ics kalendoriaus failas — atidarykite jį, kad įvykis būtų pridėtas į Outlook. RPLAW terminas nepakeičiamas.",
  "cal.prefix_deadline": "TERMINAS",
  "cal.prefix_hearing": "TEISMO POSĖDIS",
  "cal.downloaded": "Kalendoriaus failas atsiųstas",

  "invoices.title": "Sąskaitos",
  "invoices.subtitle": "Sąskaitų išrašymas iš neišrašyto laiko ir išlaidų.",
  "invoices.new": "Nauja sąskaita",
  "invoices.loading": "Kraunamos sąskaitos…",
  "invoices.empty": "Sąskaitų nerasta.",
  "invoices.search_ph": "Ieškoti pagal nr., klientą ar bylą",
  "invoices.col_number": "Sąskaitos Nr.",
  "invoices.col_date": "Data",
  "invoices.col_client": "Klientas",
  "invoices.col_matter": "Byla",
  "invoices.col_amount": "Suma",
  "invoices.col_status": "Būsena",
  "invoices.col_due": "Apmokėti iki",
  "invoices.select_matter": "Pasirinkite bylą sąskaitai.",
  "invoices.matter": "Byla",
  "invoices.continue": "Tęsti",
  "invoices.filter_all": "Visos",

  "invoice.new": "Nauja sąskaita",
  "invoice.edit_draft": "Redaguoti juodraštį {number}",
  "invoice.matter_label": "Byla",
  "invoice.details": "Sąskaitos duomenys",
  "invoice.number": "Sąskaitos numeris",
  "invoice.date": "Sąskaitos data",
  "invoice.due": "Apmokėti iki",
  "invoice.currency": "Valiuta",
  "invoice.language": "Kalba",
  "invoice.lang_lt": "Lietuvių",
  "invoice.lang_en": "Anglų",
  "invoice.language_saved": "Sąskaitos kalba atnaujinta",
  "nav.receipts": "Pinigų priėmimo kvitai",
  "receipts.title": "Pinigų priėmimo kvitai",
  "receipts.search": "Ieškoti numerio, kliento ar sąskaitos…",
  "receipts.all_statuses": "Visos būsenos",
  "receipts.number": "Kvito Nr.", "receipts.date": "Data", "receipts.client": "Klientas",
  "receipts.invoice": "Sąskaita", "receipts.amount": "Suma", "receipts.status": "Būsena",
  "receipts.empty": "Pinigų priėmimo kvitų nėra. Norėdami išrašyti pirmą kvitą, spauskite „Naujas kvitas“.",
  "receipts.actions": "Veiksmai",
  "receipts.new": "Naujas kvitas",
  "receipts.new_title": "Naujas pinigų priėmimo kvitas",
  "receipts.select_invoice": "Sąskaita (išrašyta, su neapmokėta suma)",
  "receipts.search_invoice": "Ieškoti sąskaitos nr. ar kliento…",
  "receipts.no_invoices": "Išrašytų sąskaitų nerasta",
  "receipts.change": "Keisti",
  "receipts.outstanding": "Neapmokėta suma",
  "receipts.payment_amount": "Gauta suma",
  "receipts.register": "Registruoti mokėjimą ir sukurti kvitą",
  "receipts.created": "Grynųjų mokėjimas užregistruotas, kvito juodraštis sukurtas",
  "receipts.preview": "Pinigų priėmimo kvitas",
  "receipts.print": "Spausdinti",
  "receipts.view": "Peržiūrėti",
  "receipts.payer": "Mokėtojas",
  "receipts.method": "Mokėjimo būdas",
  "receipts.created_by": "Sukūrė",
  "receipts.issued_at": "Išrašyta",
  "receipts.cancelled_info": "Anuliuota",
  "receipts.draft_hint": "Juodraštis — išrašykite kvitą, kad būtų suteiktas numeris ir sugeneruotas PDF.",
  "receipts.delete_draft": "Ištrinti juodraštį",
  "receipts.issue": "Išrašyti", "receipts.issued": "Kvitas išrašytas",
  "receipts.cancel": "Anuliuoti", "receipts.cancel_prompt": "Anuliavimo priežastis:",
  "receipts.cancelled": "Kvitas anuliuotas",
  "receipts.delete_draft_confirm": "Ištrinti šio kvito juodraštį?",
  "receipts.deleted": "Kvito juodraštis ištrintas",
  "receipts.date_from": "Data nuo", "receipts.date_to": "Data iki",
  "pay.method_bank": "Banko pavedimas", "pay.method_cash": "Grynieji pinigai",
  "pay.method_other": "Kita",
  "pay.issue_receipt": "Išrašyti pinigų priėmimo kvitą",
  "pay.receipt_created": "Sukurtas kvito juodraštis — peržiūrėkite ir išrašykite skiltyje „Pinigų priėmimo kvitai“",
  "pay.receipt": "Kvitas", "pay.create_receipt": "Išrašyti kvitą",
  "settings.receipt_numbering": "Kvitų numeracija",
  "settings.receipt_series": "Kvito serija",
  "settings.next_receipt_number": "Kitas kvito numeris",
  "invoice.delete_test_confirm": "Ištrinti TEST sąskaitą {number} (būsena: {status})? Susieti laiko ir išlaidų įrašai bus grąžinti į neapmokėtus, mokėjimai pašalinti. Veiksmas negrįžtamas.",
  "invoices.purge_test": "Ištrinti testines sąskaitas",
  "invoices.purge_confirm": "Ištrinti visas {count} TEST sąskaitas? Susieti laiko ir išlaidų įrašai bus grąžinti į neapmokėtus, mokėjimai pašalinti. Veiksmas negrįžtamas.",
  "invoices.purged": "Ištrinta testinių sąskaitų: {count}",
  "invoice.vat_treatment": "PVM režimas",
  "invoice.payment_link": "Mokėjimo nuoroda (neprivaloma)",
  "invoice.vat_note": "PVM pastaba (rodoma PDF)",
  "invoice.client": "Klientas",
  "invoice.client_code": "Kliento įmonės kodas",
  "invoice.client_vat": "Kliento PVM kodas",
  "invoice.client_address": "Kliento adresas",
  "invoice.internal_notes": "Vidinės pastabos (nerodomos PDF)",
  "invoice.unbilled_items": "Neišrašyti įrašai",
  "invoice.unbilled_time": "Neišrašyti laiko įrašai",
  "invoice.unbilled_expenses": "Neišrašytos išlaidos",
  "invoice.no_time": "Nėra neišrašytų apmokamų laiko įrašų.",
  "invoice.no_expenses": "Nėra neišrašytų klientui priskirtinų išlaidų.",
  "invoice.lines": "Sąskaitos eilutės ({n})",
  "invoice.no_lines": "Eilučių dar nepasirinkta.",
  "invoice.time_entry": "Laiko įrašas",
  "invoice.expense": "Išlaida",
  "invoice.totals_fees": "Paslaugų suma be PVM",
  "invoice.totals_vat": "PVM",
  "invoice.totals_expenses": "Kompensuojamos išlaidos",
  "invoice.totals_total": "Mokėjimo suma",
  "invoice.create_draft": "Sukurti sąskaitos juodraštį",
  "invoice.save_draft": "Išsaugoti juodraštį",
  "invoice.select_one": "Pasirinkite bent vieną laiko įrašą arba išlaidą.",
  "invoice.invalid_dates": "Įveskite teisingas sąskaitos ir apmokėjimo datas (DD.MM.YYYY).",
  "invoice.created": "Sąskaitos juodraštis {number} sukurtas",
  "invoice.updated": "Sąskaita atnaujinta",
  "invoice.only_draft_edit": "Redaguoti galima tik juodraščio būsenos sąskaitas",
  "invoice.back": "Atgal į sąskaitas",
  "invoice.download_pdf": "Atsisiųsti PDF",
  "invoice.mark_issued": "Pažymėti kaip išrašytą",
  "invoice.mark_paid": "Pažymėti kaip apmokėtą",
  "invoice.cancel_invoice": "Atšaukti sąskaitą",
  "invoice.delete": "Ištrinti",
  "invoice.release": "Atlaisvinti įrašus",
  "invoice.issue_confirm": "Išrašyti sąskaitą {number}? Įtraukti įrašai bus pažymėti kaip išrašyti.",
  "invoice.issued": "Sąskaita išrašyta",
  "invoice.paid_confirm": "Pažymėti sąskaitą {number} kaip apmokėtą?",
  "invoice.paid": "Sąskaita pažymėta apmokėta",
  "invoice.cancel_confirm": "Atšaukti sąskaitą {number}? Numeris liks istorijoje. Įrašai liks išrašytais, kol juos aiškiai atlaisvinsite.",
  "invoice.cancelled": "Sąskaita atšaukta",
  "invoice.delete_confirm": "Ištrinti sąskaitos juodraštį? Jo įrašai vėl taps neišrašytais.",
  "invoice.deleted": "Sąskaitos juodraštis ištrintas",
  "invoice.release_confirm": "Atlaisvinti išrašytus laiko įrašus ir išlaidas atgal į neišrašytus?",
  "invoice.released": "Įrašai atlaisvinti į neišrašytus",
  "invoice.released_note": "Išrašyti įrašai atlaisvinti atgal į neišrašytus.",
  "invoice.payment_link_title": "Mokėjimo nuoroda",
  "invoice.payment_saved": "Mokėjimo nuoroda išsaugota",
  "invoice.no_payment_link": "Mokėjimo nuoroda nenustatyta — PDF nieko apie mokėjimą nebus.",
  "invoice.pay": "Apmokėti sąskaitą",
  "invoice.services": "Teisinės paslaugos",
  "invoice.reimb": "Kompensuojamos išlaidos",
  "invoice.no_time_lines": "Laiko įrašai neįtraukti.",
  "invoice.no_expense_lines": "Išlaidos neįtrauktos.",
  "invoice.col_unit": "Mato vnt.",
  "invoice.col_qty": "Kiekis",
  "invoice.col_rate": "Kaina",
  "invoice.col_vat": "PVM %",
  "invoice.col_amount": "Suma be PVM",

  "settings.title": "Nustatymai",
  "settings.subtitle": "Sąskaitų išrašytojo duomenys, numeracija ir integracijos.",
  "settings.firm": "Įmonės duomenys",
  "settings.invoice_defaults": "Sąskaitų numatytieji",
  "settings.integrations": "Integracijos",
  "settings.firm_name": "Įmonės / advokato pavadinimas",
  "settings.company_code": "Įmonės / asmens kodas",
  "settings.vat_number": "PVM kodas",
  "settings.address": "Adresas",
  "settings.telephone": "Telefonas",
  "settings.email": "El. paštas",
  "settings.bank_name": "Banko pavadinimas",
  "settings.swift": "SWIFT / BIC",
  "settings.iban1": "IBAN 1",
  "settings.iban2": "IBAN 2",
  "settings.default_vat": "Numatytasis PVM tarifas (%)",
  "settings.payment_term": "Numatytoji apmokėjimo trukmė (dienos)",
  "settings.invoice_prefix": "Sąskaitos prefiksas",
  "settings.next_number": "Kitas sąskaitos numeris",
  "settings.next_suggested": "Kita siūloma sąskaita: {number}",
  "settings.everything_fields": "Everything paieškos laukai",
  "settings.ef_client": "Klientas",
  "settings.ef_name": "Bylos pavadinimas",
  "settings.ef_internal_ref": "Vidinis nr.",
  "settings.ef_case_number": "Bylos nr. teisme",
  "settings.obsidian_vault": "Numatytasis Obsidian vault pavadinimas",
  "settings.calendar_reminder": "Numatytasis kalendoriaus priminimas",
  "settings.default_language": "Numatytoji programos kalba",
  "settings.save": "Išsaugoti nustatymus",
  "settings.saved": "Nustatymai išsaugoti",
  "settings.loading": "Kraunami nustatymai…",

  "nav.reports": "Ataskaitos",
  "reports.title": "Ataskaitos",
  "reports.subtitle": "Praktikos valdymo ir atsiskaitymų ataskaitos iš esamų RPLAW duomenų.",
  "reports.type.time": "Darbo laiko ataskaita",
  "reports.type.matter-activity": "Bylos darbų ataskaita",
  "reports.type.expenses": "Išlaidų ataskaita",
  "reports.type.unbilled": "Neišrašytų darbų ataskaita",
  "reports.type.invoices": "Sąskaitų ataskaita",
  "reports.type.revenue": "Pajamų ataskaita",
  "reports.filters": "Filtrai",
  "reports.quick_range": "Greitas laikotarpis",
  "reports.range.all": "Visas laikotarpis",
  "reports.range.this_month": "Šis mėnuo",
  "reports.range.prev_month": "Praėjęs mėnuo",
  "reports.range.this_quarter": "Šis ketvirtis",
  "reports.range.this_year": "Šie metai",
  "reports.range.prev_year": "Praėję metai",
  "reports.range.custom": "Pasirinktinis laikotarpis",
  "reports.date_from": "Nuo datos",
  "reports.date_to": "Iki datos",
  "reports.client": "Klientas",
  "reports.client_ph": "Kliento pavadinimas…",
  "reports.matter": "Byla",
  "reports.all": "Visi",
  "reports.matter_status": "Bylos būsena",
  "reports.lawyer": "Atsakingas teisininkas",
  "reports.billing_status": "Atsiskaitymo būsena",
  "reports.invoice_status": "Sąskaitos būsena",
  "reports.group_by": "Grupuoti pagal",
  "reports.group_none": "Be grupavimo",
  "reports.group_client": "Klientas",
  "reports.group_matter": "Byla",
  "reports.include": "Įtraukti",
  "reports.inc_time": "Laiko įrašai",
  "reports.inc_tasks": "Užduotys",
  "reports.inc_deadlines": "Terminai",
  "reports.inc_expenses": "Išlaidos",
  "reports.inc_invoices": "Sąskaitos",
  "reports.select_matter": "Pasirinkite bylą šiai ataskaitai.",
  "reports.print": "Spausdinti",
  "reports.pdf": "PDF",
  "reports.csv": "Eksportuoti CSV",
  "reports.empty": "Pagal pasirinktus filtrus duomenų nėra.",
  "reports.loading": "Rengiama ataskaita…",
  "reports.generated": "Sugeneruota",
  "reports.period": "Laikotarpis",
  "reports.total_actual": "Viso faktinio laiko",
  "reports.total_billable": "Viso apmokamo laiko",
  "reports.total_value": "Bendra vertė",
  "reports.total_unbilled": "Neišrašyta vertė",
  "reports.total_amount": "Bendra suma",
  "reports.total_vat": "Viso PVM",
  "reports.grand_total": "Viso",
  "reports.totals": "Iš viso",
  "reports.unbilled_hours": "Neišrašytos valandos",
  "reports.unbilled_time_value": "Neišrašyto laiko vertė",
  "reports.unbilled_expenses": "Neišrašytos išlaidos",
  "reports.total_potential": "Viso potencialiai išrašytina",
  "reports.allocated_draft": "Priskirta juodraščiui {n}",
  "reports.issued_total": "Išrašytos sąskaitos",
  "reports.paid_total": "Apmokėtos sąskaitos",
  "reports.revenue_note": "Pajamos skaičiuojamos pagal sąskaitų būsenas. Juodraščiai ir atšauktos sąskaitos nėra pajamos.",
  "reports.invoice_count": "Sąskaitų skaičius",
  "reports.invoiced_total": "Išrašyta sąskaitomis",
  "reports.time_value": "Laiko vertė",
  "reports.expenses_total": "Išlaidų suma",
  "reports.col.date": "Data",
  "reports.col.client": "Klientas",
  "reports.col.matter": "Byla",
  "reports.col.description": "Aprašymas",
  "reports.col.actual": "Faktinis",
  "reports.col.billable": "Apmokamas",
  "reports.col.rate": "Įkainis",
  "reports.col.amount": "Suma",
  "reports.col.billing_status": "Atsiskaitymas",
  "reports.col.category": "Kategorija",
  "reports.col.vat": "PVM",
  "reports.col.billable_client": "Priskirtina klientui",
  "reports.col.invoice_no": "Sąskaitos Nr.",
  "reports.col.number": "Sąskaitos Nr.",
  "reports.col.invoice_date": "Sąskaitos data",
  "reports.col.due": "Apmokėti iki",
  "reports.col.fees": "Paslaugos be PVM",
  "reports.col.expenses": "Kompens. išlaidos",
  "reports.col.total": "Viso",
  "reports.col.status": "Būsena",
  "reports.col.type": "Įrašas",
  "reports.col.hours": "Valandos",
  "reports.col.value": "Vertė",
  "reports.col.allocation": "Paskirstymas",
  "reports.kind.time": "Laiko įrašas",
  "reports.kind.expense": "Išlaida",
  "reports.cat.time": "Laikas",
  "reports.cat.task": "Užduotis",
  "reports.cat.deadline": "Terminas",
  "reports.cat.expense": "Išlaida",
  "reports.cat.invoice": "Sąskaita",
  "settings.branding": "Prekės ženklas",
  "settings.invoice_logo": "Pilnas logotipas sąskaitoms",
  "settings.app_symbol": "Programos simbolis / logotipas",
  "settings.upload": "Įkelti",
  "settings.logo_uploaded": "Logotipas atnaujintas",
  "settings.logo_hint": "PNG, JPG arba WEBP iki 5 MB. Saugojama programos ištekliuose.",
  "nav.clients": "Klientai",
  "nav.import": "Duomenų importas",
  "import.title": "Duomenų importas",
  "import.subtitle": "Importuokite klientus ir istorines sąskaitas iš VMI i.SAF XML (1.2 v.) failų, įskaitant Saskaitele.lt eksportus.",
  "import.tab_new": "Naujas importas",
  "import.tab_archive": "Importuotos sąskaitos",
  "import.tab_batches": "Importų istorija",
  "import.file_label": "i.SAF XML failas",
  "import.file_hint": ".xml, iki 20 MB",
  "import.choose_file": "Pasirinkti failą",
  "import.analyze": "Analizuoti",
  "import.analyzing": "Analizuojama…",
  "import.confirm": "Patvirtinti importą",
  "import.importing": "Importuojama…",
  "import.no_changes": "Jokie duomenų bazės pakeitimai neatliekami, kol nepatvirtinsite importo.",
  "import.preview_title": "Importo peržiūra",
  "import.customers": "Klientai",
  "import.invoices": "Sąskaitos",
  "import.total": "Rasta iš viso",
  "import.new_records": "Nauji",
  "import.duplicates": "Galimi dublikatai",
  "import.invalid": "Netinkami",
  "import.incomplete": "Nepilni (be kodų)",
  "import.skipped": "Bus praleista",
  "import.reason_label": "Priežastis",
  "import.matched_with": "Sutampa su",
  "import.reason.missing_name": "Trūksta pavadinimo",
  "import.reason.missing_invoice_no": "Trūksta sąskaitos numerio",
  "import.reason.missing_invoice_date": "Trūksta sąskaitos datos",
  "import.reason.invalid_date": "Neteisinga data",
  "import.reason.registration_number": "Registracijos kodas jau egzistuoja",
  "import.reason.vat_number": "PVM kodas jau egzistuoja",
  "import.reason.name": "Pavadinimas jau egzistuoja",
  "import.reason.within_file": "Dublikatas faile",
  "import.reason.invoice_exists": "Sąskaita jau importuota",
  "import.col_name": "Pavadinimas",
  "import.col_legacy_id": "Šaltinio ID",
  "import.col_reg": "Reg. kodas",
  "import.col_vat": "PVM kodas",
  "import.col_country": "Šalis",
  "import.col_invoice_no": "Sąskaitos Nr.",
  "import.col_date": "Data",
  "import.col_type": "Tipas",
  "import.col_customer": "Klientas",
  "import.col_taxable": "Apmokestinamoji vertė",
  "import.col_vat_amount": "PVM",
  "import.col_gross": "Bendra suma",
  "import.col_currency": "Valiuta",
  "import.col_due_date": "Apmokėti iki",
  "import.result_title": "Importas baigtas",
  "import.result_customers": "Importuota klientų",
  "import.result_invoices": "Importuota sąskaitų",
  "import.result_skipped": "Praleista",
  "import.error_parse": "Nepavyko apdoroti XML failo. Įsitikinkite, kad tai tinkamas i.SAF 1.2 failas.",
  "import.error_empty": "Faile nerasta klientų ar sąskaitų.",
  "import.empty_archive": "Importuotų sąskaitų dar nėra.",
  "import.empty_batches": "Importų dar nebuvo.",
  "import.batch_file": "Failas",
  "import.batch_date": "Importuota",
  "import.search_archive": "Ieškoti pagal sąskaitos Nr. ar klientą…",
  "clients.title": "Klientai",
  "clients.subtitle": "Klientų registras, įskaitant importuotus iš i.SAF.",
  "clients.search": "Ieškoti pagal pavadinimą ar kodą…",
  "clients.empty": "Klientų dar nėra. Pradėkite importuodami iš i.SAF.",
  "clients.source": "Šaltinis",
  "clients.total": "Klientų: {n}",
  "clients.back": "Atgal į klientus",
  "clients.basic_data": "Kliento duomenys",
  "clients.imported_invoices": "Importuotos sąskaitos",
  "clients.no_invoices": "Šiam klientui importuotų sąskaitų nėra.",
  "clients.totals": "Iš viso",
  "clients.filter_no": "Filtruoti pagal sąskaitos Nr.…",
  "clients.date_from": "Data nuo",
  "clients.date_to": "Data iki",
  "clients.view": "Peržiūrėti",
  "import.col_client_link": "Klientas",
  "nav.receivables": "Gautinos sumos",
  "pay.title": "Mokėjimai",
  "pay.add": "Pridėti mokėjimą",
  "pay.edit": "Redaguoti mokėjimą",
  "pay.delete": "Ištrinti mokėjimą",
  "pay.delete_confirm": "Ištrinti šį mokėjimą?",
  "pay.date": "Mokėjimo data",
  "pay.amount": "Suma",
  "pay.method": "Mokėjimo būdas / nuoroda",
  "pay.note": "Pastaba",
  "pay.total_paid": "Iš viso apmokėta",
  "pay.outstanding": "Neapmokėta suma",
  "pay.status.unpaid": "Neapmokėta",
  "pay.status.partial": "Iš dalies apmokėta",
  "pay.status.paid": "Apmokėta",
  "pay.status.overdue": "Pradelsta",
  "pay.empty": "Mokėjimų dar nėra.",
  "pay.overpay_warning": "Mokėjimų suma ({paid}) viršytų sąskaitos sumą ({gross}). Vis tiek patvirtinti?",
  "pay.added": "Mokėjimas pridėtas",
  "pay.updated": "Mokėjimas atnaujintas",
  "pay.deleted": "Mokėjimas ištrintas",
  "pay.save": "Išsaugoti mokėjimą",
  "pay.days_overdue": "Pradelsta dienų",
  "recv.title": "Gautinos sumos",
  "recv.subtitle": "Išrašytos RPLAW sąskaitos ir jų apmokėjimo būsena. Importuotos i.SAF sąskaitos neįtrauktos.",
  "recv.filter_client": "Klientas…",
  "recv.filter_no": "Sąskaitos Nr.…",
  "recv.filter_status": "Būsena",
  "recv.all": "Visos",
  "recv.outstanding_only": "Tik neapmokėtos",
  "recv.empty": "Pagal filtrus sąskaitų nerasta.",
  "recv.col_invoice": "Sąskaitos Nr.",
  "recv.col_client": "Klientas",
  "recv.col_date": "Sąskaitos data",
  "recv.col_due": "Apmokėti iki",
  "recv.col_gross": "Bendra suma",
  "recv.col_paid": "Apmokėta",
  "recv.col_outstanding": "Neapmokėta",
  "recv.col_status": "Būsena",
  "recv.col_days_overdue": "Pradelsta dienų",
  "dashboard.stat_outstanding": "Neapmokėtos sąskaitos",
  "dashboard.stat_overdue_invoices": "Pradelstos sąskaitos",
  "reports.payments_received": "Gauti mokėjimai",
  "reports.payments_note": "Gauti mokėjimai skaičiuojami pagal faktinius mokėjimų įrašus ir nėra maišomi su sąskaitų pajamomis.",
  "reports.col_method": "Būdas",
  "clients.rplaw_invoices": "RPLAW sąskaitos",
  "clients.billed": "Išrašyta",
  "clients.paid": "Apmokėta",
  "clients.outstanding": "Neapmokėta",
  "clients.no_rplaw_invoices": "Šiam klientui RPLAW sąskaitų nėra.",
  "clients.add_matter": "Nauja byla",
  "pay.status.unknown": "Nežinoma",
  "pay.settled": "Apmokėjimas patvirtintas (be mokėjimo operacijos)",
  "imp.back": "Atgal į archyvą",
  "imp.original_data": "Originalūs apskaitos duomenys (iš i.SAF, tik skaityti)",
  "imp.payment_info": "Mokėjimo informacija (suvesta RPLAW)",
  "imp.mark_paid": "Pažymėti apmokėta",
  "imp.mark_paid_confirm": "Pažymėti sąskaitą kaip visiškai apmokėtą? Bus užfiksuotas istorinis apmokėjimo patvirtinimas be mokėjimo operacijos.",
  "imp.mark_unpaid": "Pažymėti neapmokėta",
  "imp.mark_unpaid_confirm": "Pažymėti sąskaitą neapmokėta? Neapmokėta suma taps visa bendra suma.",
  "imp.mark_unknown": "Grąžinti į „Nežinoma“",
  "imp.mark_unknown_confirm": "Grąžinti būseną į „Nežinoma“? Mokėjimų istorija išsaugoma.",
  "imp.mark_unknown_blocked": "Yra mokėjimų įrašų. Pirmiau juos suderinkite (ištrinkite), tada grąžinkite į „Nežinoma“.",
  "imp.status_set": "Būsena atnaujinta",
  "recv.filter_source": "Šaltinis",
  "recv.source.all": "Visos",
  "recv.source.rplaw": "RPLAW",
  "recv.source.isaf": "Importuotos i.SAF",
  "clients.imp_confirmed_outstanding": "Patvirtinta neapmokėta",
  "clients.imp_paid": "Apmokėta (importuotos)",
  "clients.imp_unknown": "Nežinoma būsena",
  "reports.payments_split_rplaw": "iš RPLAW sąskaitų",
  "reports.payments_split_imported": "iš importuotų sąskaitų",
  "clients.status": "Būsena",
  "clients.mark_inactive": "Pažymėti neaktyviu",
  "clients.reactivate": "Suaktyvinti",
  "clients.status_updated": "Kliento būsena atnaujinta",
  "clients.matters": "Bylos",
  "clients.no_matters": "Susietų bylų nėra.",
  "clients.created": "Klientas sukurtas",
  "clients.matter_name": "Bylos pavadinimas",
  "form.client_create_new": "Sukurti naują klientą",
  "form.client_include_inactive": "Įtraukti neaktyvius klientus",
  "form.client_create_title": "Naujas klientas",
  "form.client_no_results": "Atitinkančių klientų nerasta",
  "form.client_create_anyway": "Vis tiek sukurti",
  "matters.close": "Užbaigti bylą",
  "matters.archive": "Archyvuoti",
  "matters.reopen": "Atnaujinti bylą",
  "matters.close_confirm": "Užbaigti šią bylą? Ji liks pasiekiama ir galėsite ją atnaujinti.",
  "matters.archive_confirm": "Archyvuoti šią bylą? Ji liks pasiekiama ir galėsite ją atnaujinti.",
  "matters.closed": "Byla užbaigta",
  "matters.archived": "Byla archyvuota",
  "matters.reopened": "Byla atnaujinta",
  "matters.reopen_confirm": "Atnaujinti šią bylą? Ji vėl taps aktyvi.",
  "val.On hold": "Sustabdyta",
  "val.Archived": "Archyvuota",
  "val.Inactive": "Neaktyvus",

  "auth.sign_in": "Prisijungti",
  "auth.subtitle": "Pasiekite savo bylas ir terminus.",
  "auth.email": "El. paštas",
  "auth.password": "Slaptažodis",
  "auth.signing_in": "Jungiamasi…",
  "auth.forgot": "Pamiršote slaptažodį?",
  "auth.private": "Privatu — tik įgaliotiems naudotojams.",
  "auth.tagline1": "Kiekviena byla. Kiekvienas terminas.",
  "auth.tagline2": "Niekas nepraleidžiama.",
  "auth.tagline_body": "Privatus bylų ir terminų valdymas jūsų praktikai. Prisijunkite ir peržiūrėkite aktyvias bylas, artėjančius teismo terminus ir neatliktas užduotis.",
  "auth.reset_title": "Slaptažodžio atstatymas",
  "auth.reset_body": "Įveskite paskyros el. paštą ir atsiųsime atstatymo nuorodą.",
  "auth.send_link": "Siųsti nuorodą",
  "auth.sending": "Siunčiama…",
  "auth.check_inbox": "Patikrinkite paštą",
  "auth.reset_sent": "Jei šis el. paštas registruotas, atstatymo nuoroda išsiųsta. Nuoroda galioja 1 valandą.",
  "auth.back_login": "Atgal į prisijungimą",
  "auth.new_password": "Naujas slaptažodis",
  "auth.min_chars": "Mažiausiai 8 simboliai.",
  "auth.new_password_label": "Naujas slaptažodis",
  "auth.confirm_password": "Pakartokite slaptažodį",
  "auth.mismatch": "Slaptažodžiai nesutampa.",
  "auth.reset_btn": "Atstatyti slaptažodį",
  "auth.invalid_link": "Neteisinga nuoroda",
  "auth.missing_token": "Šioje atstatymo nuorodoje trūksta kodo. Užsisakykite naują.",
  "auth.loading": "Kraunama…",
};

const translations = { en, lt };

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem("rplaw_lang") || "lt");

  useEffect(() => {
    if (user && user.language) {
      setLang(user.language);
      localStorage.setItem("rplaw_lang", user.language);
    } else if (user && !user.language) {
      // No preference stored yet — fall back to the firm default, then local.
      api.get("/settings")
        .then((r) => {
          const def = r.data.default_ui_language;
          if (def && !localStorage.getItem("rplaw_lang")) {
            setLang(def);
            localStorage.setItem("rplaw_lang", def);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const changeLanguage = async (l) => {
    setLang(l);
    localStorage.setItem("rplaw_lang", l);
    try {
      await api.patch("/auth/preferences", { language: l });
    } catch (e) {
      /* preference sync is best-effort; local choice persists */
    }
  };

  const t = (key, vars) => {
    let s = translations[lang]?.[key] ?? translations.en[key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
    }
    return s;
  };

  return (
    <LanguageContext.Provider value={{ lang, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useT() {
  return useContext(LanguageContext);
}

export function useDaysLabel() {
  const { t } = useT();
  return (dateStr) => {
    if (!dateStr) return "";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((new Date(dateStr + "T00:00:00") - today) / 86400000);
    if (days < 0) return Math.abs(days) === 1 ? t("days.overdue_cap_one") : t("days.overdue_cap", { n: Math.abs(days) });
    if (days === 0) return t("days.due_today");
    if (days === 1) return t("days.due_tomorrow");
    return t("days.due_in_cap", { n: days });
  };
}
